import defaultData from "../../assets/data/custom.geo.json";
import React, { useRef, useEffect, useState, useCallback } from "react";
import { select, geoPath, geoMercator, scaleQuantile, selectAll, zoom, zoomIdentity, zoomTransform } from "d3";
import { useResizeObserver } from "../utility/useResizeObserver";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Helper function to generate clean export filenames for maps
const generateMapExportFilename = (title, property, year, country) => {
    if (!title) return 'MapExport';

    // Clean the title for filename use
    let filename = title
        .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

    // Add year if not already in title
    if (year && !filename.includes(year)) {
        filename += `_${year}`;
    }

    // Limit filename length
    if (filename.length > 100) {
        filename = filename.substring(0, 100);
    }

    return filename || 'MapExport';
};

const Map = ({ mapData, property, year, country, triggerExportPDF }) => {
    const svgRef = useRef();
    const wrapperRef = useRef();
    const dimensions = useResizeObserver(wrapperRef);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const projectionRef = useRef();
    const zoomBehaviorRef = useRef();
    const gRef = useRef();
    const tooltipRef = useRef();
    const originalProjectionRef = useRef(); // Store original projection parameters
    let combined = false;

    const mouseClick = (event, feature) => {
        const countryAdmin = feature.properties.admin;

        // Update the state, this will trigger the useEffect to update the mapInfo div
        if (selectedCountry === countryAdmin) {
            setSelectedFeature(null);
            setSelectedCountry(null);
        } else {
            setSelectedCountry(countryAdmin);
            setSelectedFeature(feature);
            selectAll(".country").style("opacity", 0.5);
            select(event.target).style("opacity", 1);
        }
    };

    const mouseOver = (event, feature) => {
        tooltipRef.current.style("opacity", 1);
        if (selectedCountry) return;
        gRef.current.selectAll(".country").style("opacity", 0.5);

        select(event.target).style("opacity", 1);
    };

    const mouseLeave = (event, feature) => {
        tooltipRef.current.style("opacity", 0);
        if (selectedCountry) return;
        gRef.current.selectAll(".country").style("opacity", 1);
    };

    const resetMapView = () => {
        console.log('Reset button clicked!');

        const svg = select(svgRef.current);
        const projection = projectionRef.current;
        const zoomBehavior = zoomBehaviorRef.current;
        const originalParams = originalProjectionRef.current;

        if (svg && projection && zoomBehavior && originalParams) {
            // Get current transform
            const currentTransform = zoomTransform(svg.node());
            console.log('Current transform:', currentTransform);

            // Reset the projection to its original state using stored parameters
            projection
                .scale(originalParams.scale)
                .translate(originalParams.translate);

            // Create reset transform that matches the original projection
            const resetTransform = zoomIdentity.translate(originalParams.translate[0], originalParams.translate[1]).scale(originalParams.scale);
            console.log('Reset transform:', resetTransform);
            console.log('Original params:', originalParams);

            // Update the path generator with the reset projection
            const pathGenerator = geoPath().projection(projection);
            const g = svg.select("g");
            g.selectAll("path").attr("d", pathGenerator);

            // Smoothly transition back to the reset view
            svg.transition()
                .duration(750) // 750ms smooth transition
                .call(zoomBehavior.transform, resetTransform);

            console.log('Reset transition started');
        } else {
            console.log('Missing required elements for reset');
        }
    };

    const mouseMove = (event, feature) => {
        const wrapperPosition = wrapperRef.current.getBoundingClientRect();

        const isCombined = feature.properties.combined;

        const geothermal = feature.properties.geothermal;
        const geothermal2 = feature.properties.geothermal2;
        const text1 = feature.properties.geothermalText;
        const text2 = feature.properties.geothermalText2;

        let tooltipContent = "";

        console.log(geothermal2);

        if (isCombined || geothermal2) {
            if (geothermal != null && geothermal !== "" && !isNaN(geothermal)) {
                tooltipContent += `<p>Geothermal Power - ${text1}: ${geothermal}</p>`;
            } else {
                tooltipContent += `<p>Geothermal Power: No data available for this country.</p>`;
            }

            if (geothermal2 != null && geothermal2 !== "" && !isNaN(geothermal2)) {
                tooltipContent += `<p>H&C - ${text2}: ${geothermal2}</p>`;
            } else {
                tooltipContent += `<p>H&C: No data available for this country.</p>`;
            }
        } else {
            if (geothermal != null && geothermal !== "" && !isNaN(geothermal)) {
                tooltipContent += `<p>${text1}: ${geothermal}</p>`;
            } else {
                tooltipContent += `<p>No data available for this country.</p>`;
            }
        }

        tooltipRef.current
            .html(tooltipContent)
            .style("opacity", 1)
            .style("left", `${event.clientX - wrapperPosition.left + 10}px`)
            .style("top", `${event.clientY - wrapperPosition.top + 20}px`);
    };

    useEffect(() => {
        // Use resized dimensions
        const { width, height } = dimensions || wrapperRef.current.getBoundingClientRect();

        // Projects geo-coordinates on a 2D plane
        const projection = geoMercator().fitSize([width, height], mapData).precision(1);

        projectionRef.current = projection;

        // Store original projection parameters for reset
        originalProjectionRef.current = {
            scale: projection.scale(),
            translate: projection.translate(),
            width: width,
            height: height
        };
    }, [mapData, dimensions]);

    // Will be called at any data change
    useEffect(() => {
        const svg = select(svgRef.current);

        svg.selectAll("*").remove();

        let values = [];
        let values2 = [];

        mapData.features.forEach((feature) => {
            if (feature.properties.geothermal != null && feature.properties.geothermal !== "" && !isNaN(feature.properties.geothermal) && feature.properties.geothermal) {
                values.push(feature.properties.geothermal);
            }
            if (feature.properties.geothermal2 != null && feature.properties.geothermal2 !== "" && !isNaN(feature.properties.geothermal2) && feature.properties.geothermal2) {
                values2.push(feature.properties.geothermal2);
            }
            if (feature.properties.combined) {
                combined = true;
            }
        });

        console.log(values, values2);

        let colorScale, colorScale2;

        if (property === "Total Energy used (TJ/year)" || property === "Total Installed Capacity (MWt)") {
            colorScale2 = scaleQuantile()
                .domain(values2)
                .range(["#FFEED8", "#FCD59E", "#FDBA85", "#FB8F58", "#E24A33", "#B30001"]);

            // Custom color scale
            colorScale = scaleQuantile()
                .domain(values)
                .range(["#E9FDE9", "#A4F5A4", "#66EB66", "#32D632", "#23AC23", "#188A18"]);
        } else {
            colorScale = scaleQuantile()
                .domain(values)
                .range(["#FFEED8", "#FCD59E", "#FDBA85", "#FB8F58", "#E24A33", "#B30001"]);

            // Custom color scale
            colorScale2 = scaleQuantile()
                .domain(values2)
                .range(["#E9FDE9", "#A4F5A4", "#66EB66", "#32D632", "#23AC23", "#188A18"]);
        }

        // Use resized dimensions
        const { width, height } = dimensions || wrapperRef.current.getBoundingClientRect();

        select(wrapperRef.current).selectAll(".tooltip").remove();

        // Create a tooltip
        const tooltip = select(wrapperRef.current)
            .append("div")
            .style("opacity", 0)
            .attr("class", "tooltip");

        tooltipRef.current = tooltip;

        // Projects geo-coordinates on a 2D plane
        const projection = projectionRef.current;

        // Transforms geojson data into the d attribute of a path element
        const pathGenerator = geoPath().projection(projection).pointRadius(1);

        // Create a single defs element
        const defs = svg.append("defs");

        // Define the default pattern for countries without data
        defs
            .append("pattern")
            .attr("id", "diagonalHatch")
            .attr("patternUnits", "userSpaceOnUse") // Important!
            .attr("width", 5)
            .attr("height", 5)
            .append("path")
            .attr("d", `M0,0 L5,5`)
            .attr("stroke", "#A9A9A9")
            .attr("stroke-width", "0.3px");

        // Create combined patterns based on geothermal and geothermal2
        mapData.features.forEach((feature, i) => {
            if (feature.properties.combined) {
                const pattern = defs
                    .append("pattern")
                    .attr("id", `combinedPattern${i}`)
                    .attr("patternUnits", "userSpaceOnUse")
                    .attr("width", 10)
                    .attr("height", 10);

                // Background color (e.g. H&C)
                pattern
                    .append("rect")
                    .attr("width", 10)
                    .attr("height", 10)
                    .attr("fill", colorScale2(feature.properties.geothermal2));

                // Diagonal lines (e.g. Power)
                pattern
                    .append("path")
                    .attr("d", "M-10,0 L0,10")
                    .attr("stroke", colorScale(feature.properties.geothermal))
                    .attr("stroke-width", 2);

                pattern
                    .append("path")
                    .attr("d", "M0,0 L10,10")
                    .attr("stroke", colorScale(feature.properties.geothermal))
                    .attr("stroke-width", 2);

                pattern
                    .append("path")
                    .attr("d", "M10,0 L20,10")
                    .attr("stroke", colorScale(feature.properties.geothermal))
                    .attr("stroke-width", 2);
            }
        });

        // Create a group for the map features
        const g = svg.append("g");

        // Render each country
        g.selectAll(".country")
            .data(mapData.features)
            .join("path")
            .on("click", (event, feature) => {
                mouseClick(event, feature);
            })
            .on("mouseover", (event, feature) => {
                mouseOver(event, feature);
            })
            .on("mouseleave", (event, feature) => {
                mouseLeave(event, feature);
            })
            .on("mousemove", (event, feature) => {
                mouseMove(event, feature);
            })
            .attr("class", "country")
            .style("cursor", "pointer")
            .attr("fill", (feature, i) => {
                if (feature.properties.geothermal) {
                    if (feature.properties.combined) {
                        return `url(#combinedPattern${i})`;
                    }
                    return colorScale(feature.properties.geothermal);
                } else if (feature.properties.geothermal2) {
                    return colorScale2(feature.properties.geothermal2);
                } else {
                    return "url(#diagonalHatch)";
                }
            })
            .style("stroke", "black")
            .style("stroke-width", "0.3px")
            .attr("d", (feature) => pathGenerator(feature));

        gRef.current = g;

        // Define zoom behavior
        const zoomBehavior = zoom()
            .scaleExtent([projection.scale() * 0.5, projection.scale() * 8])
            .on("zoom", (event) => {
                const { transform } = event;

                // Update the projection's translation and scale
                projection.translate([transform.x, transform.y]).scale(transform.k);

                // Update the path generator to use the updated projection
                g.selectAll("path").attr("d", pathGenerator.projection(projection));

                // No need to adjust patternTransform since patterns scale with the object
            });

        zoomBehaviorRef.current = zoomBehavior;

        // Apply zoom behavior to the SVG
        const initialTransform = zoomIdentity.translate(projection.translate()[0], projection.translate()[1]).scale(projection.scale());

        svg.call(zoomBehavior).call(zoomBehavior.transform, initialTransform);

        // Legend

        select("#legend").remove();
        select("#legend2").remove();

        if (values.length > 0) {
            const legend = select(wrapperRef.current).append("div").attr("id", "legend");

            const thresholds = colorScale.quantiles();

            colorScale.range().forEach((color, i) => {
                const legendItem = legend.append("div").attr("class", "legendItem");

                let labelText;
                if (i === 0) {
                    labelText = `0 to ${thresholds[i]?.toFixed(2)}`;
                } else if (i === thresholds.length) {
                    labelText = `> ${thresholds[i - 1]?.toFixed(2)}`;
                } else {
                    labelText = `${thresholds[i - 1]?.toFixed(2)} to ${thresholds[i]?.toFixed(2)}`;
                }

                legendItem.append("span").text(labelText);
                legendItem.append("div").attr("class", "legendColor").style("background-color", color);
            });
        }


        if (values2.length > 0) {
            const legend2 = select(wrapperRef.current).append("div").attr("id", "legend2");

            const thresholds2 = colorScale2.quantiles();

            colorScale2.range().forEach((color, i) => {
                const legendItem = legend2.append("div").attr("class", "legendItem");

                let labelText;
                if (i === 0) {
                    labelText = `0 to ${thresholds2[i].toFixed(2)}`;
                } else if (i === thresholds2.length) {
                    labelText = `> ${thresholds2[i - 1].toFixed(2)}`;
                } else {
                    labelText = `${thresholds2[i - 1].toFixed(2)} to ${thresholds2[i].toFixed(2)}`;
                }

                legendItem.append("span").text(labelText);
                legendItem.append("div").attr("class", "legendColor").style("background-color", color);
            });
        }

    }, [mapData, dimensions, property, selectedCountry]);

    // Update useEffect
    useEffect(() => {
        if (selectedCountry && selectedFeature.properties.geothermal != null) {
            // Clear any previous info
            select(".mapInfo").selectAll("*").remove();
            const info = select(".mapInfo")
                .style("transform", "translateX(0)");

            const correctedCountry = [{ 'value': selectedCountry, label: selectedCountry }];

            info.append("h2").text(getMapTitle(property, correctedCountry, year));

            // Determine labels based on whether the feature is combined
            const isCombined = selectedFeature.properties.combined;
            let value1 = selectedFeature.properties.geothermal
            let value2 = selectedFeature.properties.geothermal2;
            let text1 = selectedFeature.properties.geothermalText
            let text2 = selectedFeature.properties.geothermalText2;

            if (!value1 || value1 === NaN || value1 === '') {
                if (value2) {
                    value1 = value2;
                    text1 = text2;
                } else {
                    text1 = "No Data available for this country.";
                    value1 = '';
                }
            }

            const label1 = isCombined ? "Geothermal Power - " + text1 : text1;
            info.append("p").text(label1 + ": " + value1);

            if (isCombined) {
                const label2 = "H&C - " + text2;
                info.append("p").text(label2 + ": " + value2);
            }

            if (selectedFeature.properties.link) {
                info.append("a")
                    .attr("href", selectedFeature.properties.link)
                    .attr("target", "_blank")
                    .text("Power Source");
            }

        } else {
            // Deselect: reset styling and clear the mapInfo overlay
            selectAll(".country").style("opacity", 1);
            select(".mapInfo").style("transform", "translateX(-100%)");
            setTimeout(() => {
                select(".mapInfo").selectAll("*").remove();
            }, 1000);
        }

        // Update projection and paths
        const svg = select(svgRef.current);
        const g = svg.select("g");
        const projection = projectionRef.current;
        const zoomBehavior = zoomBehaviorRef.current;

        const pathGenerator = geoPath().projection(projection);

        // Update paths
        g.selectAll("path").attr("d", pathGenerator);

        // Update zoom behavior's internal transform
        const newTransform = zoomIdentity.translate(projection.translate()[0], projection.translate()[1]).scale(projection.scale());

        svg.call(zoomBehavior.transform, newTransform);
    }, [selectedCountry, selectedFeature, dimensions, year]);

    const handleExportPDF = useCallback(() => {
        // Reset map view and close any open panels before export
        console.log('Preparing map for export...');

        // Reset the map to original view
        const svg = select(svgRef.current);
        const projection = projectionRef.current;
        const zoomBehavior = zoomBehaviorRef.current;
        const originalParams = originalProjectionRef.current;

        if (svg && projection && zoomBehavior && originalParams) {
            // Reset the projection to its original state
            projection
                .scale(originalParams.scale)
                .translate(originalParams.translate);

            // Create reset transform
            const resetTransform = zoomIdentity.translate(originalParams.translate[0], originalParams.translate[1]).scale(originalParams.scale);

            // Update the path generator with the reset projection
            const pathGenerator = geoPath().projection(projection);
            const g = svg.select("g");
            g.selectAll("path").attr("d", pathGenerator);

            // Apply the reset transform immediately (no animation for export)
            svg.call(zoomBehavior.transform, resetTransform);
        }

        // Close any open country selection panels
        setSelectedCountry(null);
        setSelectedFeature(null);

        // Reset country opacity
        selectAll(".country").style("opacity", 1);

        // Hide any open map info panels
        select(".mapInfo").style("transform", "translateX(-100%)");

        // Hide the reset button for export
        const resetButton = document.querySelector('button[style*="position: absolute"]');
        if (resetButton) {
            resetButton.style.display = 'none';
        }

        // Wait a moment for the reset to complete, then capture
        setTimeout(() => {
            const input = document.getElementById('viz');

            html2canvas(input, {
                scale: 2, // improves clarity
                backgroundColor: '#ffffff', // solid background for JPEG
                useCORS: true,
                windowWidth: document.body.scrollWidth,
            }).then((canvas) => {
                const imgWidth = 280; // PDF width in mm
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                const bottomMargin = 20; // Add bottom margin

                const imgData = canvas.toDataURL('image/jpeg', 1); // JPEG with 70% quality

                const pdf = new jsPDF('l', 'mm', [imgWidth, imgHeight + bottomMargin]);
                pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);

                // Generate filename from title (calculate it here)
                const mapTitle = getMapTitle(property, country, year);
                const filename = generateMapExportFilename(mapTitle, property, year, country);
                pdf.save(`${filename}.pdf`);

                // Show the reset button again after export
                if (resetButton) {
                    resetButton.style.display = 'block';
                }
            }).catch((err) => {
                console.error('Map PDF export error:', err);
                alert('Map PDF export failed. Please try again or use a different browser.');

                // Show the reset button again even if export failed
                if (resetButton) {
                    resetButton.style.display = 'block';
                }
            });
        }, 500); // Wait 500ms for reset to complete
    }, [property, year, country, setSelectedCountry, setSelectedFeature]);

    useEffect(() => {
        if (triggerExportPDF) {
            triggerExportPDF(handleExportPDF);
        }
    }, [triggerExportPDF, handleExportPDF]);

    return (
        <div ref={wrapperRef} id="mapContainer" style={{ position: 'relative' }}>
            <svg ref={svgRef}></svg>
            <button
                onClick={() => {
                    console.log('Button clicked!');
                    resetMapView();
                }}
                style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    padding: '8px 12px',
                    backgroundColor: '#ff6600',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    zIndex: 1000
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#e55a00'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#ff6600'}
            >
                Reset View
            </button>
        </div>
    );
};

const getMapTitle = (activeProperty, regionArray, year) => {
    // Extract the unit from activeProperty if it’s written in parentheses…
    let unit = "";
    const unitMatch = activeProperty.match(/\(([^)]+)\)/);
    if (unitMatch) {
        unit = "";
    } else if (activeProperty === 'combinedGeothermalHcPowerProduction') {
        unit = "GWh & TJ";
    } else if (activeProperty === 'combinedGeothermalHcInstalledCapacity') {
        unit = "MWe & MWt";
    }
    // regionArray is expected to be an array from your Select control.
    const region = regionArray && regionArray.length > 0
        ? regionArray.map(c => c.label || c.value).join(', ')
        : "";

    switch (activeProperty) {
        case "Geothermal Gross Electrical generation GWh/yr":
            return `Geothermal Power - Total Gross Electrical Generation (GWh/year) in ${region} for WGC ${year}`;
        case "Geothermal Installed Capacity (MWe)":
            return `Geothermal Power - Total Installed Capacity (MWe) in ${region} for WGC ${year}`;
        case "Total Energy used (TJ/year)":
            return `Geothermal H&C - Total Energy Used (TJ/year) in ${region} for WGC ${year}`;
        case "Total Installed Capacity (MWt)":
            return `Geothermal H&C - Total Installed Capacity (MWt) in ${region} for WGC ${year}`;
        case "combinedGeothermalHcInstalledCapacity":
            return `Combined Geothermal Power and H&C Production (GWh/year & TJ/year) in ${region} for WGC ${year}`;
        case "combinedGeothermalHcPowerProduction":
            return `Combined Geothermal Power and H&C Installed Capacity (MWe & MWt) in ${region} for WGC ${year}`;
    }

    if (activeProperty === 'combinedGeothermalHcPowerProduction' || activeProperty === 'combinedGeothermalHcInstalledCapacity') {
        return `${activeProperty} (${unit}) in ${region} for WGC ${year}`;
    } else {
        return `${activeProperty} in ${region} for WGC ${year}`;
    }

};

export const Geochart = ({ data, property, year, triggerExportPDF, country }) => {
    const [start, setStart] = useState(false);
    const [mapData, setMapData] = useState(defaultData);

    useEffect(() => {
        setStart(false);
        if (data && typeof data === "object") {
            setMapData(data);
        }
    }, [data]);

    useEffect(() => {
        setTimeout(() => {
            setStart(true);
        }, 500);
    }, [mapData]);


    const title = getMapTitle(property, country, year);

    if (start) {
        return (
            <>
                <div id='fixedTitleMap'>
                    <h2 className="titles">{title}</h2>
                </div>
                <Map mapData={mapData} property={property} year={year} country={country} triggerExportPDF={triggerExportPDF} />
                <div className="mapInfo"></div>
            </>
        );
    } else {
        return <div></div>;
    }
};